// ======================================================================
// File: src/modules/ppe/pages/procurement/purchase-return/view/PurchaseReturnDetail.jsx
// Module: PPE → Procurement → Purchase Return
//
// Purpose:
// - Detailed view of a single Purchase Return
// - Shows header and return line items
// - Provides workflow actions
// - Provides Purchase Return line entry
// - Follows the existing GRNDetail.jsx structure
// ======================================================================

import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

import {
  formatDate,
  formatQuantity,
} from "../../../../../../utils/formatUtils";

import purchaseReturnService from "../services/purchaseReturnService";
import PurchaseReturnLineDrawer from "../form/PurchaseReturnLineDrawer";

export default function PurchaseReturnDetail() {

  const { id } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const [lineDrawerOpen, setLineDrawerOpen] = useState(false);

  // ----------------------------------------------------------
  // LOAD PURCHASE RETURN DETAIL
  // ----------------------------------------------------------
  const loadDetail = async () => {
    try {

      setLoading(true);

      const result =
        await purchaseReturnService.getPurchaseReturn(id);

      if (result && result.data) {
        setData(result.data);
      } else {
        setData(null);
      }

    } catch (error) {

      console.error(
        "Error loading Purchase Return details:",
        error
      );

      alert(
        error?.response?.data?.message ||
        "Failed to load Purchase Return details"
      );

    } finally {

      setLoading(false);
    }
  };

  useEffect(() => {
    loadDetail();

    // eslint-disable-next-line
  }, [id]);

  // ----------------------------------------------------------
  // WORKFLOW ACTIONS
  // ----------------------------------------------------------

  const handleSubmitForApproval = async () => {

    try {

      await purchaseReturnService.submitForApproval(id);

      alert(
        "Purchase Return submitted for approval successfully"
      );

      loadDetail();

    } catch (error) {

      alert(
        error?.response?.data?.message ||
        "Submit for approval failed"
      );
    }
  };

  const handlePost = async () => {

    try {

      await purchaseReturnService.postPurchaseReturn(id);

      alert(
        "Purchase Return posted successfully"
      );

      loadDetail();

    } catch (error) {

      alert(
        error?.response?.data?.message ||
        "Post Purchase Return failed"
      );
    }
  };

  const handleCancel = async () => {

    const reason = prompt(
      "Enter cancellation reason"
    );

    if (!reason) {
      return;
    }

    try {

      await purchaseReturnService.cancelPurchaseReturn(
        id,
        { reason }
      );

      alert(
        "Purchase Return cancelled successfully"
      );

      loadDetail();

    } catch (error) {

      alert(
        error?.response?.data?.message ||
        "Cancel failed"
      );
    }
  };

  // ----------------------------------------------------------
  // RENDER ACTIONS
  // ----------------------------------------------------------

  const renderActions = () => {

    if (!data) {
      return null;
    }

    const hasLines =
      data.lines &&
      data.lines.length > 0;

    return (
      <div
        style={{
          display: "flex",
          gap: 10,
        }}
      >

        {/* DRAFT → SUBMIT FOR APPROVAL */}
        {data.status_code === "DRAFT" && (
          <>
            <button
              className="btn btn-primary"
              onClick={handleSubmitForApproval}
              disabled={!hasLines}
            >
              Submit For Approval
            </button>

            <button
              className="btn btn-secondary"
              onClick={() => setLineDrawerOpen(true)}
            >
              + Add Line
            </button>
          </>
        )}

        {/* APPROVED → POST */}
        {data.status_code === "APPROVED" && (
          <button
            className="btn btn-warning"
            onClick={handlePost}
          >
            Post Purchase Return
          </button>
        )}

        {/* APPROVED → CANCEL */}
        {data.status_code === "APPROVED" && (
          <button
            className="btn btn-danger"
            onClick={handleCancel}
          >
            Cancel Purchase Return
          </button>
        )}

      </div>
    );
  };

  // ----------------------------------------------------------
  // LOADING
  // ----------------------------------------------------------

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!data) {
    return (
      <div>
        No Purchase Return data found
      </div>
    );
  }

  // ----------------------------------------------------------
  // MAIN RENDER
  // ----------------------------------------------------------

  return (
    <div>

      {/* ======================================================
          HEADER
          ====================================================== */}

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 20,
        }}
      >

        <div>

          <h4>
            Purchase Return Detail – {data.return_no}
          </h4>

          <div
            style={{
              fontSize: 13,
              color: "#6b7280",
            }}
          >
            Status: {data.status_name || data.status_code || "-"}
          </div>

        </div>

        <button
          className="btn btn-light"
          onClick={() => navigate(-1)}
        >
          Back
        </button>

      </div>

      {/* ======================================================
          ACTION BAR
          ====================================================== */}

      <div
        style={{
          marginBottom: 20,
        }}
      >
        {renderActions()}
      </div>

      {/* ======================================================
          HEADER INFO CARD
          ====================================================== */}

      <div className="card mb-3">

        <div className="card-body">

          <div className="row">

            <div className="col-3">
              <strong>Return Date:</strong>
              <div>
                {formatDate(data.return_date)}
              </div>
            </div>

            <div className="col-3">
              <strong>Vendor:</strong>
              <div>
                {data.vendor_name || data.vendor_id}
              </div>
            </div>

            <div className="col-3">
              <strong>Purchase Order:</strong>
              <div>
                {data.po_no || data.po_id}
              </div>
            </div>

            <div className="col-3">
              <strong>GRN:</strong>
              <div>
                {data.grn_no || data.grn_id}
              </div>
            </div>

            <div className="col-3 mt-3">
              <strong>Return Type:</strong>
              <div>
                {data.return_type || "-"}
              </div>
            </div>

            <div className="col-3 mt-3">
              <strong>Reason:</strong>
              <div>
                {data.reason || "-"}
              </div>
            </div>

            <div className="col-3 mt-3">
              <strong>Total Amount:</strong>
              <div>
                {formatQuantity(data.total_amount)}
              </div>
            </div>

            <div className="col-3 mt-3">
              <strong>Tax Amount:</strong>
              <div>
                {formatQuantity(data.tax_amount)}
              </div>
            </div>

            <div className="col-3 mt-3">
              <strong>Net Amount:</strong>
              <div>
                {formatQuantity(data.net_amount)}
              </div>
            </div>

            <div className="col-12 mt-3">
              <strong>Remarks:</strong>
              <div>
                {data.remarks || "-"}
              </div>
            </div>

          </div>

        </div>

      </div>

      {/* ======================================================
          RETURN LINES
          ====================================================== */}

      <div className="card">

        <div className="card-header">
          <strong>
            Purchase Return Lines
          </strong>
        </div>

        <div className="card-body p-0">

          <table className="table table-bordered mb-0">

            <thead className="table-light">

              <tr>
                <th>Material</th>
                <th>Return Source</th>
                <th>Return Qty</th>
                <th>UOM</th>
                <th>Rate</th>
                <th>Batch</th>
                <th>Expiry</th>
                <th>Reason</th>
              </tr>

            </thead>

            <tbody>

              {data.lines &&
                data.lines.length === 0 && (
                  <tr>
                    <td
                      colSpan={8}
                      className="text-center text-muted"
                    >
                      No return lines added
                    </td>
                  </tr>
                )}

              {data.lines &&
                data.lines.map((line) => (

                  <tr key={line.id}>

                    <td>
                      {line.material_name ||
                        line.material_id}
                    </td>

                    <td>
                      {line.return_source}
                    </td>

                    <td>
                      {formatQuantity(
                        line.return_qty
                      )}
                    </td>

                    <td>
                      {line.uom}
                    </td>

                    <td>
                      {formatQuantity(
                        line.unit_rate
                      )}
                    </td>

                    <td>
                      {line.batch_no || "-"}
                    </td>

                    <td>
                      {formatDate(
                        line.expiry_date
                      )}
                    </td>

                    <td>
                      {line.reason_code || "-"}
                    </td>

                  </tr>

                ))}

            </tbody>

          </table>

        </div>

      </div>

      {/* ======================================================
          PURCHASE RETURN LINE DRAWER
          ====================================================== */}

      <PurchaseReturnLineDrawer
        open={lineDrawerOpen}
        purchaseReturnId={id}
        grnId={data.grn_id}
        onClose={() => setLineDrawerOpen(false)}
        onSaved={loadDetail}
      />

    </div>
  );
}